<template>
  <v-card :class="['glass-card-theme', isDarkMode ? 'dark-theme' : 'light-theme']" style="border-radius: 16px; overflow: hidden">
    <!-- Header -->
    <div class="dialog-header d-flex align-center justify-space-between pa-5">
      <div class="d-flex align-center">
        <v-icon color="green" class="mr-3" large>mdi-school</v-icon>
        <div>
          <h3 class="text-h6 font-weight-black mb-0" :class="isDarkMode ? 'white--text' : 'black--text'">
            {{ item._id ? "Schulferien bearbeiten" : "Schulferien erstellen" }}
          </h3>
          <span class="text-caption grey--text">Konfigurieren Sie den Ferienzeitraum</span>
        </div>
      </div>
      <v-btn icon :dark="isDarkMode" @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-divider class="border-light"></v-divider>

    <!-- Content -->
    <v-card-text class="pa-6">
      <v-container class="pa-0">
        <!-- Titel -->
        <v-row class="mb-4">
          <v-col cols="12" class="py-1">
            <v-text-field
              v-model="item.name"
              label="Ferienbezeichnung / Titel"
              placeholder="z.B. Sommerferien"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg"></v-text-field>
          </v-col>
        </v-row>

        <!-- Datumsauswahl -->
        <v-row class="mb-4">
          <v-col cols="12" sm="6" class="py-1">
            <v-text-field
              v-model="item.startDate"
              type="date"
              label="Startdatum"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg"></v-text-field>
          </v-col>

          <v-col cols="12" sm="6" class="py-1">
            <v-text-field
              v-model="item.endDate"
              type="date"
              label="Enddatum"
              outlined
              dense
              hide-details
              :dark="isDarkMode"
              class="rounded-lg"></v-text-field>
          </v-col>
        </v-row>

        <!-- Validation Alerts -->
        <div v-if="validation.errors.length > 0" class="mb-4">
          <v-alert v-for="(err, idx) in validation.errors" :key="'err-' + idx" type="error" outlined dense class="rounded-lg mb-0 text-caption">
            {{ err }}
          </v-alert>
        </div>

        <!-- Summary Panel / Dauer -->
        <v-row
          v-if="item.startDate && item.endDate && validation.errors.length === 0"
          class="summary-block py-3 rounded-lg mx-0 mb-2"
          style="background: rgba(128, 128, 128, 0.05)">
          <v-col cols="12" class="text-center py-1">
            <div class="text-caption grey--text">Dauer der Ferien</div>
            <div class="text-h6 font-weight-black green--text">{{ duration }} {{ duration === 1 ? "Tag" : "Tage" }}</div>
            <div class="text-caption grey--text mt-1">von {{ formatDate(item.startDate) }} bis {{ formatDate(item.endDate) }}</div>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>

    <!-- Footer Actions -->
    <v-divider class="border-light"></v-divider>
    <v-card-actions class="pa-4 bg-actions d-flex justify-end gap-2">
      <v-btn text color="grey" class="font-weight-bold rounded-lg px-4" @click="$emit('close')"> Abbrechen </v-btn>
      <v-btn
        color="green"
        class="white--text font-weight-bold rounded-lg px-6"
        :disabled="validation.errors.length > 0 || !item.name"
        @click="saveItem">
        <v-icon left>mdi-content-save</v-icon> Speichern
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "schoolHolidaysDialog",
  props: {
    existingValue: {
      type: Object,
      default: () => ({ _id: null }),
    },
  },
  data() {
    return {
      item: {
        name: "",
        shortName: "",
        startDate: "",
        endDate: "",
      },
    }
  },
  computed: {
    isDarkMode() {
      return localStorage.getItem("shiftManagementDarkMode") !== "false"
    },
    duration() {
      if (this.item.startDate && this.item.endDate) {
        const start = new Date(this.item.startDate)
        const end = new Date(this.item.endDate)
        const diffTime = end.getTime() - start.getTime()
        if (diffTime < 0) {
          return 0
        }
        return Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1
      }
      return 0
    },
    validation() {
      let errors = []
      if (this.item.startDate && this.item.endDate) {
        const start = new Date(this.item.startDate)
        const end = new Date(this.item.endDate)
        if (end.getTime() < start.getTime()) {
          errors.push("Das Enddatum darf nicht vor dem Startdatum liegen.")
        }
      }
      return { errors }
    },
  },
  created() {
    if (this.existingValue._id) {
      this.item = { ...this.existingValue }
    }
  },
  methods: {
    saveItem() {
      if (this.validation.errors.length > 0 || !this.item.name) return
      this.$emit("input", this.item)
    },
    formatDate(date) {
      const parts = date.split("-")
      if (parts.length === 3) {
        return parts[2] + "." + parts[1] + "." + parts[0]
      }
      return date
    },
  },
}
</script>

<style scoped>
.glass-card-theme {
  transition: all 0.3s ease;
}
.glass-card-theme.dark-theme {
  background: rgba(26, 26, 26, 0.98) !important;
  color: #ffffff !important;
  border: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.glass-card-theme.light-theme {
  background: rgba(255, 255, 255, 0.98) !important;
  color: #333333 !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}

.dialog-header {
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.12), rgba(0, 0, 0, 0));
}
.dark-theme .dialog-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.light-theme .dialog-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
  background: linear-gradient(135deg, rgba(76, 175, 80, 0.08), rgba(255, 255, 255, 0));
}

.border-light {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.light-theme .border-light {
  border-color: rgba(0, 0, 0, 0.08) !important;
}

.bg-actions {
  background: rgba(0, 0, 0, 0.1);
}
.light-theme .bg-actions {
  background: rgba(0, 0, 0, 0.02);
}

.gap-2 {
  gap: 8px;
}
</style>
